// src/app/core/chatPage/chatCopmonents/chatInForm/components/ChatTabs.tsx
import { useState, useRef, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { TextGenerationViewModel } from '@/app/core/chatPage/chatCopmonents/shared/TextGenerationViewModel';
import type { Message } from '@/app/core/chatPage/chatCopmonents/shared/index';
import { Header } from './Header';
import { MessagesList } from './MessagesList';
import { InputArea } from './InputArea';
import { ConversationSidebar } from './ConversationSidebar';
import { ModelSelector } from './ModelSelector';
import { AgentSettingsModal } from '../components/agent/AgentSettingsModal';
import { AnimatePresence, motion } from 'motion/react';

interface ChatTabsProps {
  messages: Message[];
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  modelDisplayNames: Record<string, string>;
  activeConversationTitle?: string | null;
  onRegenerate: (messageId: string) => void;
  onScroll: () => void;
  initialTab?: 'create' | 'chats' | 'models';
}

function ContextIndicator({ used, total }: { used: number; total: number }) {
  const percentUsed = total > 0 ? (used / total) * 100 : 0;
  const remainPercent = Math.max(0, 100 - percentUsed);
  const color = remainPercent > 50 ? 'rgb(34,197,94)' : remainPercent > 20 ? 'rgb(234,179,8)' : 'rgb(239,68,68)';

  return (
    <div className="relative w-5 h-5 shrink-0" title={`Осталось ${Math.round(remainPercent)}% контекста`}>
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: `conic-gradient(${color} ${remainPercent * 3.6}deg, rgba(255,255,255,0.15) 0deg)`,
        }}
      />
      <div className="absolute inset-[3px] rounded-full bg-white dark:bg-gray-900" />
    </div>
  );
}

export const ChatTabs = observer(({
  messages,
  scrollContainerRef,
  messagesEndRef,
  modelDisplayNames,
  activeConversationTitle,
  onRegenerate,
  onScroll,
  initialTab,
}: ChatTabsProps) => {
  const navigate = useNavigate();
  const viewModel = TextGenerationViewModel.getInstance();
  const [activeTab, setActiveTab] = useState<'create' | 'chats' | 'models'>(initialTab || 'create');
  const [agentSettingsOpen, setAgentSettingsOpen] = useState(false);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const touchStartX = useRef(0);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.model-selector-area')) return;
    touchStartX.current = e.changedTouches[0].screenX;
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.model-selector-area')) return;
    const delta = touchStartX.current - e.changedTouches[0].screenX;
    if (Math.abs(delta) < 50) return;
    if (delta > 0) {
      if (activeTab === 'create') setActiveTab('chats');
      else if (activeTab === 'chats') setActiveTab('models');
    } else {
      if (activeTab === 'models') setActiveTab('chats');
      else if (activeTab === 'chats') setActiveTab('create');
    }
  }, [activeTab]);

  // Просто выходим из чата на главную (или назад, если есть история)
  const handleNavigateBack = () => {
    viewModel.clearDraftText(); // очищаем черновик при выходе
    if (window.history.length > 1 && window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleSend = (text: string) => {
    if (viewModel.isGenerating) {
      viewModel.stopGeneration();
    }
    viewModel.handleSend(text);
  };

  const handleExampleClick = (prompt: string) => {
    viewModel.handleSend(prompt);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'create':
        return (
          <motion.div
            key="create"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col h-full"
          >
            <div className="flex-1 min-h-0 flex flex-col">
              {/* Заголовок активного диалога */}
              {activeConversationTitle && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="shrink-0 px-4 py-2 border-b border-white/10"
                >
                  <div className="flex items-center gap-0">
                    <h3 className="text-sm font-semibold text-center truncate flex-1">
                      {activeConversationTitle}
                    </h3>
                    {viewModel.contextWindowTokens != null && viewModel.contextUsedTokens != null && (
                      <ContextIndicator used={viewModel.contextUsedTokens} total={viewModel.contextWindowTokens} />
                    )}
                  </div>
                </motion.div>
              )}

              {/* Анимированное переключение загрузка/сообщения */}
              <AnimatePresence mode="wait">
                {viewModel.isLoadingMessages ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-1 flex items-center justify-center px-6 py-10"
                  >
                    <div className="flex items-center gap-2">
                      {[0, 1, 2].map((i) => (
                        <motion.span
                          key={i}
                          className="h-2.5 w-2.5 rounded-full bg-foreground/60"
                          animate={{
                            scale: [1, 1.5, 1],
                            opacity: [0.3, 1, 0.3],
                          }}
                          transition={{
                            duration: 1.2,
                            repeat: Infinity,
                            repeatType: 'loop',
                            ease: 'easeInOut',
                            delay: i * 0.2,
                          }}
                        />
                      ))}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="messages"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="flex-1 min-h-0 flex flex-col"
                  >
                    <MessagesList
                      messages={messages}
                      messagesEndRef={messagesEndRef}
                      modelDisplayNames={modelDisplayNames}
                      selectedModelName={viewModel.selectedModel.name}
                      activeConversationTitle={activeConversationTitle}
                      onRegenerate={onRegenerate}
                      onScroll={onScroll}
                      containerRef={scrollContainerRef}
                      className="flex-1 min-h-0 overflow-y-auto"
                      onExampleClick={handleExampleClick}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="shrink-0">
                <InputArea
                  onSend={handleSend}
                  onStop={() => viewModel.stopGeneration()}
                />
              </div>
            </div>
          </motion.div>
        );
      case 'chats':
        return (
          <motion.div
            key="chats"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="h-full"
          >
            <ConversationSidebar
              conversations={viewModel.conversations ?? []}
              activeConversationId={viewModel.activeConversationDbId}
              onSelectConversation={(conv) => {
                viewModel.selectConversation(conv);
                setActiveTab('create');
              }}
              onStartNewConversation={() => {
                viewModel.startNewConversation();
                setActiveTab('create');
              }}
              onDeleteConversation={(id) => viewModel.deleteConversation(id)}
              onRenameConversation={(id, newTitle) => viewModel.renameConversation(id, newTitle)}
              isFullPage
            />
          </motion.div>
        );
      case 'models':
        return (
          <motion.div
            key="models"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="h-full"
          >
            <ModelSelector onModelSelected={() => {}} />
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-transparent">
      <Header
        onNavigateBack={handleNavigateBack}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onOpenProSettings={() => setAgentSettingsOpen(true)}
      />
      <div
        className="flex-1 flex flex-col relative min-h-0"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence mode="wait">
          {renderTabContent()}
        </AnimatePresence>
      </div>

      <AgentSettingsModal
        open={agentSettingsOpen}
        onClose={() => setAgentSettingsOpen(false)}
      />
    </div>
  );
});